import {Popover, PopoverContent, PopoverTrigger} from "../ui/popover";
import {ChevronsUpDown, ChevronRight} from "lucide-react";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "../ui/command";
import {cn} from "../ui/lib/utils";
import React, {ReactNode, useEffect, useRef, useState} from "react";
import {DropDownItem} from "./MultiSelectorDropdown/MultiSelectorDropDown";

const DefaultDisplaySettings: CategorizedSearchableDropdownDisplaySettings = {
    smallSize: true,
    borderClassName: "border",
    className: ""
}

class CategorizedSearchableDropdownDisplaySettings {
    smallSize: boolean = false
    borderClassName: string = "border"
    className: string = ""
    dropdownSide?: "top" | "bottom" = "bottom"
    dropdownAlign?: "start" | "center" | "end" = "end"
    dropdownAlignOffset?: number = 0
    dropdownSideOffset?: number = 5
}

export interface Category {
    name: string;
    items: DropDownItem[];
}

export interface CategorizedSearchableDropdownProps {
    displaySettings?: CategorizedSearchableDropdownDisplaySettings;
    noMatchString: ReactNode;
    categories: Category[];
    searchPlaceholder: string;
    selected: DropDownItem;
    setSelected: (item: DropDownItem) => void;
    allowAnyString?: boolean;
    disableWildcard?: boolean;
    disabled?: boolean;
}

export function CategorizedSearchableDropdown(props: CategorizedSearchableDropdownProps) {
    const [openDropdown, setOpenDropdown] = React.useState(false);
    const [currSearch, setCurrSearch] = React.useState("");
    const [filteredCategories, setFilteredCategories] = useState<Category[]>(props.categories);
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
    const [showCategories, setShowCategories] = useState(true);
    const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
    const [lastNavigationMethod, setLastNavigationMethod] = React.useState<'mouse' | 'keyboard'>('mouse');
    const listRef = useRef<HTMLDivElement>(null);

    let displaySettings = props.displaySettings || DefaultDisplaySettings;

    // Get all items from all categories for search
    const allItems = props.categories.flatMap(category => category.items);

    // Filter categories based on search
    useEffect(() => {
        if (currSearch !== undefined && currSearch !== "") {
            setShowCategories(false);
            // Filter items across all categories based on search text
            const filteredItems = allItems.filter((item) => 
                item.displayName?.toLowerCase().includes(currSearch.toLowerCase())
            );
            
            // Add custom search string as the first item if allowAnyString is true
            let finalItems = filteredItems;
            if (props.allowAnyString && currSearch !== "*") {
                // Check if there's an exact match
                const exactMatch = filteredItems.find(item => 
                    item.displayName?.toLowerCase() === currSearch.toLowerCase()
                );
                
                if (!exactMatch) {
                    // Add the custom search string as the first item
                    finalItems = [{
                        displayName: `${currSearch} (custom)`,
                        value: currSearch
                    }, ...filteredItems];
                }
            }
            
            // Group filtered items back into a single "Search Results" category
            setFilteredCategories([{
                name: "Search Results",
                items: finalItems
            }]);
            setHighlightedIndex(finalItems.length > 0 ? 0 : -1);
            setLastNavigationMethod('keyboard');
        } else {
            setShowCategories(true);
            setFilteredCategories(props.categories);
            setHighlightedIndex(-1);
        }
    }, [currSearch, props.categories, props.allowAnyString]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current && lastNavigationMethod === 'keyboard') {
            const highlightedElement = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
            if (highlightedElement) {
                highlightedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, lastNavigationMethod]);

    // Clear search when dropdown is opened
    const handleOpenChange = (open: boolean) => {
        if (props.disabled) return;
        
        setOpenDropdown(open);
        if (open) {
            setCurrSearch("");
            setHighlightedIndex(-1);
            setLastNavigationMethod('mouse');
            setHoveredCategory(null);
        }
    };

    // Get all visible items for keyboard navigation
    const getVisibleItems = (): DropDownItem[] => {
        if (showCategories && hoveredCategory) {
            const category = filteredCategories.find(cat => cat.name === hoveredCategory);
            return category ? category.items : [];
        }
        return filteredCategories.flatMap(cat => cat.items);
    };

    // Handle command input key events (for keyboard navigation)
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const items = getVisibleItems();

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                if (showCategories && !hoveredCategory) {
                    // Navigate through categories
                    setHoveredCategory(filteredCategories[0]?.name || null);
                } else {
                    // Navigate through items
                    setHighlightedIndex(prev => 
                        prev < items.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                if (showCategories && hoveredCategory && highlightedIndex === -1) {
                    // Go back to category view
                    setHoveredCategory(null);
                } else {
                    // Navigate through items
                    setHighlightedIndex(prev => 
                        prev > 0 ? prev - 1 : items.length - 1
                    );
                }
                break;
            case 'ArrowRight':
                if (showCategories && hoveredCategory) {
                    e.preventDefault();
                    setHighlightedIndex(0);
                }
                break;
            case 'ArrowLeft':
                if (showCategories && hoveredCategory && highlightedIndex >= 0) {
                    e.preventDefault();
                    setHoveredCategory(null);
                    setHighlightedIndex(-1);
                }
                break;
            case 'Enter':
                e.preventDefault();

                // Select the highlighted item if present
                if (highlightedIndex >= 0 && highlightedIndex < items.length) {
                    const selectedItem = items[highlightedIndex];
                    // If this is a custom item, extract the actual value without "(custom)" suffix
                    if (selectedItem.displayName?.endsWith(' (custom)')) {
                        props.setSelected({
                            displayName: selectedItem.value,
                            value: selectedItem.value
                        });
                    } else {
                        props.setSelected(selectedItem);
                    }
                    setOpenDropdown(false);
                } else if (props.allowAnyString && currSearch && currSearch !== "*") {
                    // If no item is highlighted but we have a search string, use it as custom
                    const exactMatch = allItems.find(item => 
                        item.displayName?.toLowerCase() === currSearch.toLowerCase()
                    );

                    if (!exactMatch) {
                        props.setSelected({
                            displayName: currSearch,
                            value: currSearch
                        });
                        setOpenDropdown(false);
                    }
                }
                break;
        }
    };

    return <div className={cn("items-center flex flex-col grow justify-center", displaySettings.className)}>
        <Popover open={openDropdown} modal={true} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild
                            className={cn("flex",
                                displaySettings.smallSize ? "h-5" : "",
                                props.displaySettings?.borderClassName
                            )}>
                <div
                    role="combobox"
                    aria-expanded={openDropdown}
                    aria-disabled={props.disabled}
                    className={cn("flex w-full items-center justify-between text-textmedium px-2 h-[32px]",
                        displaySettings.smallSize ? "text-xs h-3" : "",
                        props.disabled ? "opacity-50 cursor-not-allowed" : "hover:cursor-pointer hover:text-white"
                        )}
                    onClick={() => !props.disabled && handleOpenChange(!openDropdown)}
                >
                    {props.selected.displayName || "Select a unit"}
                    <ChevronsUpDown
                        className={cn("ml-2 h-4 w-4 shrink-0 opacity-50",
                            displaySettings.smallSize ? "h-2 w-2" : "")}/>
                </div>
            </PopoverTrigger>
            <PopoverContent side={props.displaySettings? props.displaySettings?.dropdownSide : "bottom"}
                            align={props.displaySettings ? props.displaySettings?.dropdownAlign : "end"}
                            alignOffset={props.displaySettings ? props.displaySettings?.dropdownAlignOffset : 0}
                            sideOffset={props.displaySettings ? props.displaySettings?.dropdownSideOffset : 5}
                            avoidCollisions={true}
                            onFocusOutside={() => setOpenDropdown(false)}
                            onEscapeKeyDown={() => setOpenDropdown(false)}
                            onInteractOutside={() => setOpenDropdown(false)}
                            className="text-textlight bg-backgroundmedium w-[600px] rounded p-0">
                <Command onKeyDown={handleKeyDown} shouldFilter={false}>
                    <CommandInput 
                        placeholder={props.searchPlaceholder} 
                        value={currSearch} 
                        id={"categorized_unit_input"}
                        onChangeCapture={(e) => {
                            setCurrSearch(e.currentTarget.value)
                        }}
                        className={cn("h-12 grow text-textlight shadow-none bg-backgroundmedium")}
                        autoFocus
                    />
                    <CommandList ref={listRef} className={"text-textlight overflow-hidden"}>
                        <CommandEmpty>
                            {props.noMatchString}
                        </CommandEmpty>
                        
                        {showCategories ? (
                            // Show categories with hover navigation
                            <div className="flex h-[300px]">
                                {/* Categories sidebar */}
                                <div className="w-52 border-r border-border overflow-y-auto no-scrollbar flex-shrink-0">
                                    <CommandGroup>
                                        {filteredCategories.map((category, categoryIndex) => (
                                            <CommandItem
                                                key={category.name}
                                                className={cn(
                                                    "w-full justify-between cursor-pointer",
                                                    hoveredCategory === category.name && "bg-secondarytransparenter border-r-2 border-r-secondary"
                                                )}
                                                onMouseEnter={() => {
                                                    setHoveredCategory(category.name);
                                                    setHighlightedIndex(-1);
                                                    setLastNavigationMethod('mouse');
                                                }}
                                                onSelect={() => {
                                                    // Don't close on category selection
                                                }}
                                            >
                                                <span>{category.name}</span>
                                                <ChevronRight className="h-4 w-4" />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </div>
                                
                                {/* Units for hovered category */}
                                <div className="flex-1 min-w-0 overflow-y-auto no-scrollbar">
                                    {hoveredCategory && (
                                        <CommandGroup>
                                            {filteredCategories
                                                .find(cat => cat.name === hoveredCategory)
                                                ?.items.map((item, index) => {
                                                    const isHighlighted = highlightedIndex === index;
                                                    
                                                    return <CommandItem
                                                        className={cn(
                                                            "w-full",
                                                            isHighlighted && "bg-secondarytransparenter border border-secondary",
                                                            lastNavigationMethod === 'mouse' && "hover:bg-secondarytransparenter hover:border hover:border-secondary"
                                                        )}
                                                        key={index}
                                                        data-index={index}
                                                        onMouseEnter={() => {
                                                            setHighlightedIndex(index);
                                                            setLastNavigationMethod('mouse');
                                                        }}
                                                        onSelect={() => {
                                                            setOpenDropdown(false);
                                                            props.setSelected(item);
                                                        }}>
                                                        {item.displayName}
                                                    </CommandItem>
                                                })}
                                        </CommandGroup>
                                    )}
                                    {!hoveredCategory && (
                                        <div className="p-4 text-center text-textdark text-sm">
                                            Hover over a category to see units
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            // Show search results
                            <CommandGroup>
                                {filteredCategories[0]?.items.map((item, index) => {
                                    const isHighlighted = highlightedIndex === index;
                                    
                                    return <CommandItem
                                        className={cn(
                                            "w-full",
                                            isHighlighted && "bg-secondarytransparenter border border-secondary",
                                            lastNavigationMethod === 'mouse' && "hover:bg-secondarytransparenter hover:border hover:border-secondary"
                                        )}
                                        key={index}
                                        data-index={index}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(index);
                                            setLastNavigationMethod('mouse');
                                        }}
                                        onSelect={() => {
                                            setOpenDropdown(false);
                                            // If this is a custom item, extract the actual value without "(custom)" suffix
                                            if (item.displayName?.endsWith(' (custom)')) {
                                                props.setSelected({
                                                    displayName: item.value,
                                                    value: item.value
                                                });
                                            } else {
                                                props.setSelected(item);
                                            }
                                        }}>
                                        {item.displayName}
                                    </CommandItem>
                                })}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    </div>
}